import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectAccountInfo } from '@features/settings/store/payments.duck';
import { SwitchCounter, SwitchCounterContainer } from '@shared/ui/molecules/SwitchCounter';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { PlanInfoCard } from './PlanInfoCard';
import { PriceBreakdown } from './PriceBreakdown';
import { useTranslation } from 'react-i18next';
import { stripePackSize, stripeUserLimit } from '@shared/services/EnvironmentVariables';

// Fallback free-user allowance used only if account info hasn't supplied freeUsers.
const BASE_USERS = 10;

interface Props {
  // Selected number of paid USER seats. Held in users (not licences) so the seat-limit maths
  // below (free allowance, headcount) stays in user units; the dialog converts to packs at the
  // Stripe API boundary.
  value: number;
  onChange: (value: number) => void;
}

// The counter displays and reports paid USER seats, stepping one pack (stripePackSize users)
// at a time. Min/max are pack-aligned so clamping always lands on a multiple of the pack size.
export function ManageSeatsSection({ value, onChange }: Props) {
  const accountInfo = useSelector(selectAccountInfo);
  const { t } = useTranslation();

  const usersPerLicence = stripePackSize;
  const freeUsers = accountInfo?.freeUsers ?? BASE_USERS;
  // Paid users occupying spaces beyond the free allowance.
  const licencesInUse = Math.ceil(Math.max(0, (accountInfo?.totalUsers ?? 0) - freeUsers));

  const minLicences = Math.max(1, Math.ceil(licencesInUse / usersPerLicence)) * usersPerLicence;
  const maxLicences = Math.max(minLicences, Math.floor((stripeUserLimit - freeUsers) / usersPerLicence) * usersPerLicence);
  const selectedLicences = Math.round(value);

  if (!accountInfo) {
    return null;
  }

  return (
    <Container>
      <BodySmall weight={600} style={{ margin: '0px auto 8px auto', textAlign: 'center' }}>{t('payments.manage-seats')}</BodySmall>
      <SwitchCounter onChange={(seats: number) => onChange(seats)}
                     value={selectedLicences}
                     limit={maxLicences}
                     minValue={minLicences}
                     increment={usersPerLicence} />
      <PlanInfoCard weight={600} small>{t('payments.licences-in-use', { used: licencesInUse, total: selectedLicences })}</PlanInfoCard>
      <PriceBreakdown licences={selectedLicences} billTotal={accountInfo.billTotal} />
    </Container>
  );
}

const Container = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  ${SwitchCounterContainer} {
    margin-bottom: 32px;
  }
`;
